import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  shorthands,
  Text,
  Card,
  Button,
  Spinner,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Tooltip,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../components/Common/ScrollableMessageBar';
import { useScrollableMessage } from '../hooks/useScrollableMessage';
import {
  ArrowSync24Regular,
  PeopleTeam24Regular,
  Delete24Regular,
  ArrowLeftRegular } from
'@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px')
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    ...shorthands.margin('0', '0', '24px', '0')
  },
  actionButtons: {
    display: 'flex',
    ...shorthands.gap('12px')
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px')
  },
  card: {
    ...shorthands.padding('24px')
  },
  tableContainer: {
    overflowX: 'auto',
    marginTop: '16px'
  },
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding('48px'),
    color: tokens.colorNeutralForeground3
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px')
  }
});

interface SecurityGroup {
  id: string;
  displayName: string;
  description: string;
  createdDateTime: string;
  memberCount: number;
  members: Array<{
    id: string;
    displayName: string;
    userPrincipalName: string;
  }>;
}

export const SecurityGroups: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<SecurityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useScrollableMessage<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<SecurityGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useScrollableMessage<string | null>(null);

  const loadSecurityGroups = async () => {
    try {
      setError(null);
      const response: any = await apiClient.get('/policies/adm-security-groups');
      const data = response.data?.data || response.data;
      setGroups(data.groups || []);
    } catch (err: any) {
      console.error('Failed to load security groups:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load security groups');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadSecurityGroups();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadSecurityGroups();
  };

  const handleDeleteClick = (group: SecurityGroup) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    setDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.delete(`/policies/adm-security-groups/${groupToDelete.id}`);
      setSuccess(`Security group "${groupToDelete.displayName}" deleted successfully`);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      // Reload the groups list
      await loadSecurityGroups();
    } catch (err: any) {
      console.error('Failed to delete security group:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete security group');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={styles.container} data-testid="security-groups-container-div">
        <div className={styles.header} data-testid="security-groups-header-div">
          <Text className={styles.title} data-testid="security-groups-loading-adm-security-groups-text">
            <PeopleTeam24Regular data-testid="security-groups-loading-people-team24-regular" />
            ADM Security Groups
          </Text>
        </div>
        <div className={styles.loadingContainer} data-testid="security-groups-loading-container-div">
          <Spinner size="large" label="Loading security groups..." data-testid="security-groups-loading-security-groups-spinner" />
        </div>
      </div>);

  }

  return (
    <div className={styles.container} data-testid="security-groups-container-2-div">
      <div className={styles.header} data-testid="security-groups-header-2-div">
        <Text className={styles.title} data-testid="security-groups-main-adm-security-groups-text">
          <PeopleTeam24Regular data-testid="security-groups-main-people-team24-regular" />
          ADM Security Groups
        </Text>
        <div className={styles.actionButtons} data-testid="security-groups-action-buttons-div">
          <Button
            appearance="primary"
            icon={<ArrowSync24Regular data-testid="security-groups-arrow-sync24-regular" />}
            onClick={handleRefresh}
            disabled={refreshing} data-testid="security-groups-refresh-button">

            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            appearance="secondary"
            icon={<ArrowLeftRegular data-testid="security-groups-arrow-left-regular" />}
            onClick={() => navigate('/settings')} data-testid="security-groups-back-to-settings-button">

            Back to Settings
          </Button>
        </div>
      </div>

      {error &&
      <MessageBar intent="error" data-testid="security-groups-error-message-bar">
          <MessageBarBody data-testid="security-groups-error-message-bar-body">{error}</MessageBarBody>
        </MessageBar>
      }

      {success &&
      <MessageBar intent="success" data-testid="security-groups-success-message-bar">
          <MessageBarBody data-testid="security-groups-success-message-bar-body">{success}</MessageBarBody>
        </MessageBar>
      }

      <Card className={styles.card} data-testid="security-groups-card">
        <div style={{ marginBottom: '16px' }} data-testid="security-groups-summary-div">
          <Text style={{ fontSize: '20px', fontWeight: '600' }} data-testid="security-groups-security-groups-text">
            Security Groups ({groups.length})
          </Text>
          <Text style={{ fontSize: '14px', color: tokens.colorNeutralForeground2, marginTop: '8px', display: 'block' }} data-testid="security-groups-created-by-the-adm-text">
            Security groups created by the ADM tool for policy assignments. These groups are automatically managed and contain users based on policy assignment analysis.
          </Text>
        </div>

        {groups.length === 0 ?
        <div className={styles.emptyState} data-testid="security-groups-empty-state-div">
            <PeopleTeam24Regular style={{ fontSize: '48px', marginBottom: '16px' }} data-testid="security-groups-empty-state-people-team24-regular" />
            <Text style={{ fontSize: '16px', fontWeight: '600', display: 'block', marginBottom: '8px' }} data-testid="security-groups-no-adm-security-groups-found-text">
              No ADM Security Groups Found
            </Text>
            <Text style={{ fontSize: '14px' }} data-testid="security-groups-will-appear-here-when-text">
              Security groups will appear here when you create policies using the Assignment Workflow Analysis feature.
            </Text>
          </div> :

        <div className={styles.tableContainer} data-testid="security-groups-table-container-div">
            <Table size="small" data-testid="security-groups-table">
              <TableHeader data-testid="security-groups-table-header">
                <TableRow data-testid="security-groups-table-row">
                  <TableHeaderCell style={{ fontWeight: '600', minWidth: '250px' }} data-testid="security-groups-group-name-table-header-cell">
                    Group Name
                  </TableHeaderCell>
                  <TableHeaderCell style={{ fontWeight: '600', minWidth: '300px' }} data-testid="security-groups-description-table-header-cell">
                    Description
                  </TableHeaderCell>
                  <TableHeaderCell style={{ fontWeight: '600', textAlign: 'center', minWidth: '100px' }} data-testid="security-groups-members-table-header-cell">
                    Members
                  </TableHeaderCell>
                  <TableHeaderCell style={{ fontWeight: '600', minWidth: '180px' }} data-testid="security-groups-created-table-header-cell">
                    Created
                  </TableHeaderCell>
                  <TableHeaderCell style={{ fontWeight: '600', minWidth: '280px' }} data-testid="security-groups-group-id-table-header-cell">
                    Group ID
                  </TableHeaderCell>
                  <TableHeaderCell style={{ fontWeight: '600', textAlign: 'center', width: '80px' }} data-testid="security-groups-actions-table-header-cell">
                    Actions
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="security-groups-table-body">
                {groups.map((group, index) =>
              <TableRow key={group.id} data-testid={`security-groups-table-row-${index}`}>
                    <TableCell data-testid="security-groups-group-name-table-cell">
                      <Text style={{ fontWeight: '500' }} data-testid="security-groups-group-name-text">{group.displayName}</Text>
                    </TableCell>
                    <TableCell data-testid="security-groups-description-table-cell">
                      <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground2 }} data-testid="security-groups-description-text">
                        {group.description || '-'}
                      </Text>
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }} data-testid="security-groups-members-table-cell">
                      {group.memberCount > 0 && group.members && group.members.length > 0 ?
                  <Tooltip
                    content={
                    <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '8px' }} data-testid="security-groups-members-tooltip-div">
                              <Text style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }} data-testid="security-groups-members-text">
                                Members ({group.memberCount}):
                              </Text>
                              {group.members.map((member, memberIndex) =>
                      <div key={member.id} style={{ marginBottom: '6px' }} data-testid={`security-groups-member-item-div-${memberIndex}`}>
                                  <Text style={{ display: 'block', fontSize: '13px', fontWeight: '500' }} data-testid={`security-groups-member-name-text-${memberIndex}`}>
                                    {member.displayName}
                                  </Text>
                                  <Text style={{ display: 'block', fontSize: '12px', color: tokens.colorNeutralForeground3 }} data-testid={`security-groups-member-upn-text-${memberIndex}`}>
                                    {member.userPrincipalName}
                                  </Text>
                                </div>
                      )}
                            </div>
                    }
                    relationship="description" data-testid="security-groups-tooltip">

                          <Badge appearance="filled" color="brand" style={{ cursor: 'help' }} data-testid="security-groups-members-tooltip-badge">
                            {group.memberCount}
                          </Badge>
                        </Tooltip> :

                  <Badge appearance="filled" color="brand" data-testid="security-groups-members-badge">
                          {group.memberCount}
                        </Badge>
                  }
                    </TableCell>
                    <TableCell data-testid="security-groups-created-table-cell">
                      <Text style={{ fontSize: '13px' }} data-testid="security-groups-created-date-text">
                        {formatDate(group.createdDateTime)}
                      </Text>
                    </TableCell>
                    <TableCell data-testid="security-groups-group-id-table-cell">
                      <Text style={{ fontSize: '12px', fontFamily: 'monospace', color: tokens.colorNeutralForeground3 }} data-testid="security-groups-group-id-text">
                        {group.id}
                      </Text>
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }} data-testid="security-groups-actions-table-cell">
                      <Button
                    icon={<Delete24Regular data-testid="security-groups-delete24-regular" />}
                    appearance="subtle"
                    size="small"
                    onClick={() => handleDeleteClick(group)}
                    title="Delete security group"
                    aria-label="Delete security group" data-testid="security-groups-delete-security-group-button" />

                    </TableCell>
                  </TableRow>
              )}
              </TableBody>
            </Table>
          </div>
        }
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, data) => !data.open && handleDeleteCancel()} data-testid="security-groups-dialog">
        <DialogSurface data-testid="security-groups-dialog-surface">
          <DialogBody data-testid="security-groups-dialog-body">
            <DialogTitle data-testid="security-groups-delete-security-group-dialog-title">Delete Security Group</DialogTitle>
            <DialogContent data-testid="security-groups-dialog-content">
              <Text data-testid="security-groups-are-you-sure-you-want-to-text">
                Are you sure you want to delete the security group <strong data-testid="security-groups-delete-target-name-strong">"{groupToDelete?.displayName}"</strong>?
              </Text>
              <Text style={{ marginTop: '12px', display: 'block', color: tokens.colorPaletteRedForeground1 }} data-testid="security-groups-this-action-cannot-be-undone-the-text">
                This action cannot be undone. The group will be permanently removed from Azure AD.
              </Text>
            </DialogContent>
            <DialogActions data-testid="security-groups-dialog-actions">
              <Button appearance="secondary" onClick={handleDeleteCancel} disabled={deleting} data-testid="security-groups-cancel-button">
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm} disabled={deleting} data-testid="security-groups-confirm-delete-button">
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>);

};